/**
 * Relay reconnect policy (mobile-only, pure helpers).
 *
 * The PWA must recover by itself when the PC daemon restarts, the Worker relay
 * drops the socket, or the browser suspends the page. The transport lives in
 * `lib/api/relay.ts`; this module owns the deterministic policy so it can be
 * unit-tested without timers/sockets:
 *
 * - bounded exponential backoff with jitter (base · 2^attempt, capped);
 * - a hard cap on consecutive attempts (no infinite retry storm on a phone);
 * - the browser `online` / `visibilitychange` decisions.
 *
 * No secrets (token, keys, envelopes) are accepted or returned here.
 */

export interface ReconnectPolicy {
  /** First retry delay in ms (doubles per attempt). */
  baseMs: number;
  /** Upper bound for the exponential part (jitter is added on top). */
  maxMs: number;
  /** Extra random jitter in ms; keeps reconnect herds apart. */
  jitterMs: number;
  /** Consecutive auto-reconnect attempts before the client gives up. */
  maxAttempts: number;
}

export const DEFAULT_RECONNECT_POLICY: ReconnectPolicy = {
  baseMs: 1_000,
  maxMs: 15_000,
  jitterMs: 500,
  // ~1+2+4+8+15+15+15+15 ≈ 75s of automatic recovery before giving up.
  maxAttempts: 8,
};

/** Never exponentiate past this (the cap already dominates). */
const MAX_EXPONENT = 10;

function finiteOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * Delay before retry number `attempt` (0-based). The exponential part is
 * capped at `maxMs`; jitter is bounded to `[0, jitterMs)`. Deterministic when a
 * `random` source is injected (tests pass `() => 0`).
 */
export function reconnectDelay(
  attempt: number,
  policy: ReconnectPolicy = DEFAULT_RECONNECT_POLICY,
  random: () => number = Math.random,
): number {
  const safeAttempt = Math.max(0, Math.floor(finiteOr(attempt, 0)));
  const base = Math.max(0, finiteOr(policy.baseMs, DEFAULT_RECONNECT_POLICY.baseMs));
  const max = Math.max(base, finiteOr(policy.maxMs, DEFAULT_RECONNECT_POLICY.maxMs));
  const jitterMs = Math.max(0, finiteOr(policy.jitterMs, 0));
  const exponential = Math.min(base * 2 ** Math.min(safeAttempt, MAX_EXPONENT), max);
  let roll = 0;
  try {
    roll = finiteOr(random(), 0);
  } catch {
    roll = 0;
  }
  const jitter = Math.floor(Math.min(1, Math.max(0, roll)) * jitterMs);
  return Math.round(Math.max(0, exponential + jitter));
}

/** Whether another automatic attempt is allowed after `attempt` failures. */
export function canReconnect(
  attempt: number,
  policy: ReconnectPolicy = DEFAULT_RECONNECT_POLICY,
): boolean {
  const used = Math.max(0, Math.floor(finiteOr(attempt, 0)));
  const cap = Math.max(0, Math.floor(finiteOr(policy.maxAttempts, 0)));
  return used < cap;
}

/**
 * A page that comes back to the foreground may hold a half-open socket the
 * browser never closed (mobile suspend / network switch). Reconnect when the
 * relay is not connected, or when the app was hidden long enough that the
 * transport almost certainly went stale. Brief app switches keep the socket.
 */
export const VISIBILITY_RECONNECT_MIN_HIDDEN_MS = 5_000;

export function shouldReconnectOnVisible(input: {
  /** Relay socket currently reports connected. */
  connected: boolean;
  /** Time the page spent hidden before this event. */
  hiddenForMs: number;
}): boolean {
  if (!input.connected) return true;
  return Math.max(0, finiteOr(input.hiddenForMs, 0)) >= VISIBILITY_RECONNECT_MIN_HIDDEN_MS;
}
